/**
 * InitStores Component and Hooks
 *
 * This module handles the initialization of all Zustand stores before the application renders.
 * It ensures that global state (language, project info, etc.) and locale data are loaded
 * and ready before the app UI is displayed.
 *
 * The component displays a loading spinner (XCircleLoader) while initialization is in progress,
 * and renders the application children once all stores are initialized.
 */

import { XCircleLoader } from "components";
import type { JSX } from "react";
import React, { useEffect, useRef, useState } from "react";
import { initCookieConsentStore } from "stores/cookieConsentStore";
import { initGlobalStore } from "stores/globalStore";
import { initLocalsStore } from "stores/localsStore";
import { initProjectStore, type TreeRef } from "stores/project";

/**
 * InitStores Component
 *
 * Wrapper component that initializes all application stores before rendering children.
 * Shows a loading spinner while initialization is in progress.
 *
 * @param {React.ReactNode} children - The application content to render after initialization
 * @returns {JSX.Element} Either a loading spinner or the children, depending on initialization status
 *
 * @example
 * <InitStores>
 *   <App />
 * </InitStores>
 */
export const InitStores = ({
  children,
}: {
  children: React.ReactNode;
}): JSX.Element => {
  const isReady = useInitializeStores();

  // Show loading spinner while stores are being initialized
  if (!isReady) {
    return <XCircleLoader />;
  }

  // Render children once all stores are ready
  return <>{children}</>;
};

/**
 * useInitializeStores Hook
 *
 * Manages the store initialization process. Runs once on component mount,
 * initializing all required stores and returning a boolean flag indicating
 * whether initialization is complete.
 *
 * @returns {boolean} True when all stores have been successfully initialized, false otherwise
 *
 * @example
 * const isReady = useInitializeStores();
 * if (!isReady) {
 *   return <LoadingSpinner />;
 * }
 */
export const useInitializeStores = (): boolean => {
  const treeRef: TreeRef = useRef(null);
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    // Initialize all stores on component mount (runs once due to empty dependency array)
    initializeStores(treeRef).then(() => setIsReady(true));
  }, []);

  return isReady;
};

/**
 * Initializes all Zustand stores required by the application
 *
 * Order matters: globalStore must be initialized before localsStore
 * since localsStore depends on the language setting from globalStore.
 *
 * @returns {Promise<void>}
 */
const initializeStores = async (treeRef: TreeRef): Promise<void> => {
  // Initialize global store (sets language, project info, etc.)
  initCookieConsentStore();
  initGlobalStore();
  initProjectStore(treeRef);
  // Initialize locals store (loads translations based on language from globalStore)
  await initLocalsStore();
};
